"use client";

// Account dropdown in the top-right of the site header. Clicking the
// hamburger icon reveals Pairing / Billing / Group / Sign out. (The
// avatar + name live on the left of the header, beside the server pill.)

import { useEffect, useRef, useState, type CSSProperties } from "react";
import { VoiceSettings } from "./voice-settings";

export function AccountMenu({
  currentGroupId,
}: {
  currentGroupId?: string | null;
}) {
  const [open, setOpen] = useState(false);
  const [voiceOpen, setVoiceOpen] = useState(false);
  // Stays true through the close animation; flipped off on animationend so
  // the menu can slide out (mirroring the slide-in) before it unmounts.
  const [render, setRender] = useState(false);
  const [busy, setBusy] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) setRender(true);
  }, [open]);

  // The menu opens on click, so close it on an outside click or Escape.
  useEffect(() => {
    if (!open) return;
    const onPointer = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const itemStyle: CSSProperties = {
    display: "block",
    width: "100%",
    textAlign: "left",
    padding: "0.5rem 0.85rem",
    color: "var(--fg)",
    fontSize: "1.08rem",
    background: "transparent",
    border: "none",
    borderBottom: "1px solid #333",
    cursor: "pointer",
    textDecoration: "none",
    whiteSpace: "nowrap",
  };

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      {/* Touch-target sizing for the hamburger: ≥44px on narrow viewports
          (icon stays 26px, padding grows to hit iOS HIG / Material). */}
      <style>{`
        @media (max-width: 640px) {
          .account-menu-toggle {
            min-width: 44px !important;
            min-height: 44px !important;
            padding: 0.5rem !important;
            justify-content: center !important;
          }
        }
      `}</style>
      <button
        type="button"
        className="account-menu-toggle"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account menu"
        title="Account menu"
        style={{
          display: "inline-flex",
          alignItems: "center",
          background: open ? "var(--bg-elev)" : "transparent",
          border: "none",
          cursor: "pointer",
          color: "var(--fg)",
          padding: "0.25rem 0.4rem",
          borderRadius: 6,
        }}
      >
        <svg
          aria-hidden
          width="26"
          height="26"
          viewBox="0 0 24 24"
          style={{ display: "block", color: "var(--fg)", flexShrink: 0 }}
        >
          {/* Animates between a hamburger (closed) and an X (open): the
              top/bottom bars converge to center and rotate ±45°, the
              middle bar fades out. */}
          <line
            x1="4" y1="7" x2="20" y2="7"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round"
            style={{
              transformBox: "fill-box", transformOrigin: "center",
              transition: "transform 0.2s ease",
              transform: open ? "translateY(5px) rotate(45deg)" : "none",
            }}
          />
          <line
            x1="4" y1="12" x2="20" y2="12"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round"
            style={{ transition: "opacity 0.2s ease", opacity: open ? 0 : 1 }}
          />
          <line
            x1="4" y1="17" x2="20" y2="17"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round"
            style={{
              transformBox: "fill-box", transformOrigin: "center",
              transition: "transform 0.2s ease",
              transform: open ? "translateY(-5px) rotate(-45deg)" : "none",
            }}
          />
        </svg>
      </button>

      {render ? (
        <>
          <style>{
            "@keyframes accountMenuSlideIn{from{transform:translateX(110%)}to{transform:translateX(0)}}" +
            "@keyframes accountMenuSlideOut{from{transform:translateX(0)}to{transform:translateX(110%)}}"
          }</style>
        <div
          role="menu"
          onAnimationEnd={() => { if (!open) setRender(false); }}
          style={{
            position: "absolute",
            // Sit flush against the bottom edge of the header bar (its
            // 0.75rem bottom padding) — no gap, no overlap.
            top: "calc(100% + 0.75rem)",
            // Header has 1.5rem right padding; offset it so the menu sits
            // flush against the right edge of the screen.
            right: "-1.5rem",
            minWidth: 160,
            background: "var(--bg-elev)",
            border: "1px solid var(--border)",
            borderRadius: 0,
            boxShadow: "0 8px 24px rgba(0,0,0,0.35)",
            padding: 0,
            overflow: "hidden",
            zIndex: 200,
            animation: open
              ? "accountMenuSlideIn 0.22s ease-out"
              : "accountMenuSlideOut 0.18s ease-in forwards",
          }}
        >
          <a href="/map" style={itemStyle}>Map</a>
          <a href="/shops/search" style={itemStyle}>Shop search</a>
          <a href="/pairing" style={itemStyle}>Pairing</a>
          <a href="/billing" style={itemStyle}>Billing</a>
          {currentGroupId ? (
            <a href={`/group/${currentGroupId}`} style={itemStyle}>Group</a>
          ) : null}
          {currentGroupId ? (
            <a href="/automations" style={itemStyle}>IFTT</a>
          ) : null}
          <button
            type="button"
            style={itemStyle}
            onClick={() => {
              setOpen(false);
              setVoiceOpen(true);
            }}
          >
            Voice announcements
          </button>
          <button
            type="button"
            disabled={busy}
            style={{ ...itemStyle, color: "var(--fg-muted)", borderBottom: "none" }}
            onClick={async () => {
              setBusy(true);
              await fetch("/api/auth/steam/logout", { method: "POST" });
              window.location.href = "/";
            }}
          >
            {busy ? "Signing out…" : "Sign out"}
          </button>
        </div>
        </>
      ) : null}
      <VoiceSettings open={voiceOpen} onClose={() => setVoiceOpen(false)} />
    </div>
  );
}
